import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import {
    DUE_UNIT_HOURS_ID,
    DUE_UNIT_DAYS_ID,
    DUE_UNIT_BUSINESS_DAYS_ID,
    DUE_UNIT_MINUTES_ID,
} from 'nutshell-core/date-time/delays';
import type {DueDateUnit as DelayUnit} from 'nutshell-core/types';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import {DelayInput} from 'shells/delay-bar/delay-input';
import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {FormField} from 'shells/form/form-field';
import {Label} from 'shells/form/label';
import {
    COMPANY_MERGE_FIELD_OPTIONS,
    RECIPIENT_MERGE_FIELD_OPTIONS,
    SENDER_MERGE_FIELD_OPTIONS,
} from 'shells/textfield/consts';
import {TextfieldWithCharacterLimit} from 'shells/textfield/textfield-with-character-limit';

import type {
    GetTextAutomation_textAutomation,
    TextAutomationCreateInput,
    TextAutomationUpdateInput,
    TextAutomationDeleteInput,
} from 'nutshell-graphql-types';

import {SalesAutomationSidebarFormWrapper} from '../../../core/sidebar/sales-automation-sidebar-form-wrapper';

import type {NutshellState} from '../../../../store';

import styles from '../sales-process-form.module.css';

type Props = {
    stageId: string;
    isFetchingLatestVersion: boolean;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => any;
    textAutomation: GetTextAutomation_textAutomation | null | undefined;
    createTextAutomation: (arg1: TextAutomationCreateInput) => Promise<any>;
    updateTextAutomation: (arg1: TextAutomationUpdateInput) => Promise<any>;
    deleteTextAutomation: (arg1: TextAutomationDeleteInput) => Promise<any>;
};

const DEFAULT_DELAY_VALUE = 30;
const DEFAULT_DELAY_UNIT = DUE_UNIT_MINUTES_ID;

const DEFAULT_ASSIGNEE_ID = 'OWNER';
const LEAD_ASSIGNEE_OPTION = {id: DEFAULT_ASSIGNEE_ID, name: 'Lead assignee'} as const;
const MERGE_FIELD_OPTIONS = [
    ...RECIPIENT_MERGE_FIELD_OPTIONS,
    ...COMPANY_MERGE_FIELD_OPTIONS,
    ...SENDER_MERGE_FIELD_OPTIONS,
];

function getDelayValueFromTextAutomation(
    textAutomation?: GetTextAutomation_textAutomation | null
): number {
    const delayValue = textAutomation && textAutomation.delay && textAutomation.delay.timespan;
    if (!delayValue) return DEFAULT_DELAY_VALUE;

    return delayValue;
}

function getDelayUnitFromTextAutomation(
    textAutomation?: GetTextAutomation_textAutomation | null
): DelayUnit {
    const delayUnit = textAutomation && textAutomation.delay && textAutomation.delay.unit;
    if (!delayUnit) return DEFAULT_DELAY_UNIT;

    return delayUnit;
}

function makeTextAutomationDelayFromDelayOptions(delayValue: number, delayUnit: DelayUnit) {
    return {
        unit: delayUnit,
        timespan: delayValue,
    };
}

function validateDelayOptions(delayValue: number, delayUnit: DelayUnit): boolean {
    if (delayUnit === 'mm' && delayValue < 5) {
        return false;
    }

    return true;
}

export function FormTextAutomationsComponent(props: Props) {
    const assignables = useSelector((state: NutshellState) =>
        Assignables.getEnabledUsersByPermission(state, 'canUseSms')
    );

    const [messageContent, setMessageContent] = React.useState(
        props.textAutomation ? props.textAutomation.content : ''
    );
    const [sendFromUserId, setSendForUserId] = React.useState<string>(
        props.textAutomation && props.textAutomation.sendFromUser
            ? props.textAutomation.sendFromUser.id
            : DEFAULT_ASSIGNEE_ID
    );
    const [delayValue, setDelayValue] = React.useState(
        props.textAutomation
            ? getDelayValueFromTextAutomation(props.textAutomation)
            : DEFAULT_DELAY_VALUE
    );
    const [delayUnit, setDelayUnit] = React.useState(
        props.textAutomation
            ? getDelayUnitFromTextAutomation(props.textAutomation)
            : DEFAULT_DELAY_UNIT
    );

    const handleSaveForm = () => {
        const delayIsValid = validateDelayOptions(delayValue, delayUnit);

        if (props.textAutomation && messageContent) {
            if (!delayIsValid) return;

            return props.updateTextAutomation({
                automationId: props.textAutomation.id,
                sendFromUserId: sendFromUserId === DEFAULT_ASSIGNEE_ID ? null : sendFromUserId,
                delay: makeTextAutomationDelayFromDelayOptions(delayValue, delayUnit),
                snoozeDuration: null,
                messageContent: messageContent,
            });
        } else if (messageContent) {
            if (!delayIsValid) return;

            return props.createTextAutomation({
                stageId: props.stageId,
                messageContent: messageContent,
                sendFromUserId: sendFromUserId === DEFAULT_ASSIGNEE_ID ? null : sendFromUserId,
                delay: makeTextAutomationDelayFromDelayOptions(delayValue, delayUnit),
            });
        }
    };

    return (
        <SalesAutomationSidebarFormWrapper
            id={props.textAutomation ? props.textAutomation.id : null}
            isFetchingLatestVersion={props.isFetchingLatestVersion}
            onSaveForm={handleSaveForm}
            onDelete={{
                deleteText: 'Remove this automation',
                deleteConfirmationTitle: 'Are you sure you want to delete this text automation?',
                // @ts-expect-error
                onDeleteConfirm: (textAutomationId) => {
                    return props.deleteTextAutomation({
                        id: textAutomationId,
                    });
                },
            }}
        >
            <div className={styles.subheader}>
                When a lead enters this stage, automatically send a text message
            </div>
            <div className='flex align-start'>
                <Label htmlFor='message-content-field'>Send a text message</Label>
            </div>
            <FormField innerId='message-content-field'>
                <TextfieldWithCharacterLimit
                    // @ts-expect-error
                    name='messageContent'
                    placeholder='Type a message to be sent to the lead…'
                    onChange={setMessageContent}
                    value={messageContent}
                    maximumCharacters={255}
                    mergeFieldOptions={MERGE_FIELD_OPTIONS}
                />
            </FormField>
            <Label htmlFor='delay-config'>Start sending…</Label>
            <FormField
                innerId='delay-config'
                errorMessage={
                    !validateDelayOptions(delayValue, delayUnit)
                        ? 'Delays need to be 5 minutes or longer'
                        : undefined
                }
            >
                <div>
                    <div className={styles['delay-container']}>
                        <DelayInput
                            customOptions={[
                                {
                                    label: delayValue === 1 ? 'Minute' : 'Minutes',
                                    value: DUE_UNIT_MINUTES_ID,
                                },
                                {
                                    label: delayValue === 1 ? 'Hour' : 'Hours',
                                    value: DUE_UNIT_HOURS_ID,
                                },
                                {
                                    label: delayValue === 1 ? 'Day' : 'Days',
                                    value: DUE_UNIT_DAYS_ID,
                                },
                                {
                                    label: delayValue === 1 ? 'Business day' : 'Business days',
                                    value: DUE_UNIT_BUSINESS_DAYS_ID,
                                },
                            ]}
                            inputValue={delayValue}
                            selectedOption={delayUnit}
                            onInputChange={(newValue) => {
                                setDelayValue(newValue);
                            }}
                            onSelectChange={(newUnit) => {
                                setDelayUnit(newUnit);
                            }}
                        />
                    </div>
                    <p className={styles['delay-description']}>after the lead enters stage</p>
                </div>
            </FormField>
            <Label htmlFor='email-sender'>Send text from…</Label>
            <FormField innerId='email-sender'>
                <AssigneeSelect
                    value={sendFromUserId}
                    inputId='email-sender'
                    options={assignables}
                    customOptions={[LEAD_ASSIGNEE_OPTION]}
                    isClearable={sendFromUserId !== DEFAULT_ASSIGNEE_ID}
                    onChange={(newAssigneeId) => {
                        setSendForUserId(newAssigneeId || DEFAULT_ASSIGNEE_ID);
                    }}
                />
            </FormField>
            <CrossBrowserHiddenFormSubmit inputRef={props.formSubmitBtnRef} />
        </SalesAutomationSidebarFormWrapper>
    );
}
